import React, { useState } from 'react';
import { AnimatePresence } from 'motion/react';
import {
  ReferralPageWrapper, DentistSection, PatientSection, FormFooter, FormSection,
  SuccessScreen, RadioGroup, Fw, FieldError, icls,
  DENTIST_DEFAULTS, PATIENT_DEFAULTS, FOOTER_DEFAULTS,
  validateCommon, submitReferral,
} from './ReferralBase';

const REASON_OPTIONS = [
  { value: 'primary', label: 'Primary endodontic treatment' },
  { value: 'retreatment', label: 'Re-treatment' },
  { value: 'complex', label: 'Complex anatomy' },
  { value: 'separation', label: 'Instrument separation' },
  { value: 'calcified', label: 'Calcified canals' },
  { value: 'persistent', label: 'Persistent symptoms' },
  { value: 'diagnostic', label: 'Diagnostic opinion only' },
];

const SYMPTOM_OPTIONS = [
  { value: 'asymptomatic', label: 'Asymptomatic' },
  { value: 'biting', label: 'Pain on biting' },
  { value: 'spontaneous', label: 'Spontaneous pain' },
  { value: 'sensitivity', label: 'Hot/cold sensitivity' },
  { value: 'swelling', label: 'Swelling' },
  { value: 'sinus', label: 'Sinus tract' },
];

const RESTORABILITY_OPTIONS = [
  { value: 'restorable', label: 'Considered restorable' },
  { value: 'possibly', label: 'Possibly restorable' },
  { value: 'unsure', label: 'Unsure' },
];

const URGENCY_OPTIONS = [
  { value: 'routine', label: 'Routine' },
  { value: 'urgent', label: 'Urgent (pain/swelling)' },
  { value: 'consultation', label: 'Diagnostic consultation only' },
];

const DEFAULTS = {
  ...DENTIST_DEFAULTS, ...PATIENT_DEFAULTS,
  medicalHistory: '', toothNumbers: '',
  reasonForReferral: '', currentSymptoms: '', symptomDuration: '',
  restorability: '', restorabilityNotes: '',
  urgency: '', specialInstructions: '',
  ...FOOTER_DEFAULTS,
};

function validate(form) {
  const errors = validateCommon(form);
  if (!form.toothNumbers.trim()) errors.toothNumbers = 'Required';
  if (!form.reasonForReferral) errors.reasonForReferral = 'Please select a reason';
  if (!form.urgency) errors.urgency = 'Please select urgency';
  return errors;
}

export default function EndodonticsReferral() {
  const [form, setForm] = useState(DEFAULTS);
  const [errors, setErrors] = useState({});
  const [focused, setFocused] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors((err) => ({ ...err, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    try { await submitReferral(form, 'Endodontics'); } catch (_) {}
    setSubmitting(false);
    setSubmitted(true);
  };

  const reset = () => { setForm(DEFAULTS); setErrors({}); setSubmitted(false); };
  const inputCls = (f) => icls(f, errors, focused);

  const commonProps = { form, errors, focused, onChange: handleChange, onFocus: setFocused, onBlur: () => setFocused(null) };

  return (
    <ReferralPageWrapper
      title="Endodontics Referral"
      subtitle="Refer your patient for specialist root canal treatment, re-treatment, or endodontic consultation."
      photo="/images/referral/referral-endodontics.jpg">
      <h2 className="font-serif text-forest text-2xl font-semibold mb-1">Endodontics Referral Form</h2>
      <p className="text-gray-500 text-sm mb-7">All fields marked <span className="text-red-400">*</span> are required.</p>

      <AnimatePresence mode="wait">
        {submitted ? (
          <SuccessScreen key="success" patientName={form.patientName} onReset={reset} />
        ) : (
          <form key="form" onSubmit={handleSubmit} noValidate>
            <DentistSection {...commonProps} />
            <PatientSection {...commonProps} />

            <FormSection title="Clinical Information">
              <div className="grid gap-5">
                <Fw>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="toothNumbers">
                    Areas of Interest / Tooth Numbers <span className="text-red-400">*</span>
                  </label>
                  <input id="toothNumbers" name="toothNumbers" type="text" value={form.toothNumbers}
                    onChange={handleChange} placeholder="e.g. UL1, LR6"
                    onFocus={() => setFocused('toothNumbers')} onBlur={() => setFocused(null)}
                    className={inputCls('toothNumbers')} />
                  <FieldError error={errors.toothNumbers} />
                </Fw>
                <Fw>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="medicalHistory">
                    Relevant Medical History
                  </label>
                  <textarea id="medicalHistory" name="medicalHistory" rows={3} value={form.medicalHistory}
                    onChange={handleChange} placeholder="Include allergies, medications, medical conditions…"
                    onFocus={() => setFocused('medicalHistory')} onBlur={() => setFocused(null)}
                    className={`${inputCls('medicalHistory')} resize-none`} />
                </Fw>
                <RadioGroup name="reasonForReferral" label="Reason for Referral" required
                  options={REASON_OPTIONS} value={form.reasonForReferral}
                  onChange={handleChange} error={errors.reasonForReferral} />
                <RadioGroup name="currentSymptoms" label="Current Symptoms"
                  options={SYMPTOM_OPTIONS} value={form.currentSymptoms}
                  onChange={handleChange} error={errors.currentSymptoms} />
                <Fw>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="symptomDuration">
                    Duration of Symptoms
                  </label>
                  <input id="symptomDuration" name="symptomDuration" type="text" value={form.symptomDuration}
                    onChange={handleChange} placeholder="e.g. 2 weeks, 3 months"
                    onFocus={() => setFocused('symptomDuration')} onBlur={() => setFocused(null)}
                    className={inputCls('symptomDuration')} />
                </Fw>
              </div>
            </FormSection>

            <FormSection title="Treatment Assessment">
              <div className="grid gap-5">
                <RadioGroup name="restorability" label="Restorability Assessment"
                  options={RESTORABILITY_OPTIONS} value={form.restorability}
                  onChange={handleChange} error={errors.restorability} />
                {(form.restorability === 'possibly' || form.restorability === 'unsure') && (
                  <Fw>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="restorabilityNotes">
                      Please provide details
                    </label>
                    <textarea id="restorabilityNotes" name="restorabilityNotes" rows={2} value={form.restorabilityNotes}
                      onChange={handleChange} placeholder="Describe your restorability concerns…"
                      onFocus={() => setFocused('restorabilityNotes')} onBlur={() => setFocused(null)}
                      className={`${inputCls('restorabilityNotes')} resize-none`} />
                  </Fw>
                )}
                <RadioGroup name="urgency" label="Urgency" required
                  options={URGENCY_OPTIONS} value={form.urgency}
                  onChange={handleChange} error={errors.urgency} />
              </div>
            </FormSection>

            <FormSection title="Additional Information">
              <Fw>
                <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="specialInstructions">
                  Special Instructions
                </label>
                <textarea id="specialInstructions" name="specialInstructions" rows={3} value={form.specialInstructions}
                  onChange={handleChange} placeholder="Any other information relevant to this referral…"
                  onFocus={() => setFocused('specialInstructions')} onBlur={() => setFocused(null)}
                  className={`${inputCls('specialInstructions')} resize-none`} />
              </Fw>
            </FormSection>

            <FormFooter {...commonProps} submitting={submitting} />
          </form>
        )}
      </AnimatePresence>
    </ReferralPageWrapper>
  );
}
